import React, { FC, useMemo } from "react";
import {
  Button,
  ButtonSizeType,
  ButtonStyleType,
  CourseType,
  ICourse,
  ProductType
} from "@into-discovery/browsewebshared";
import MainProgramName from "/app/_components/atoms/search/main-program-name/main-program-name";
import PathwayOptions from "/app/_components/molecules/pathway-options/pathway-options";
import { Accordion } from "react-bootstrap";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "/navigation";
import UniversityName from "/app/_components/atoms/search/university-name/university-name";
import color from "../../../../../styles/_helpers/branding.module.scss";
import { getSearchResultURL, SearchResultUrls } from "../../../../../services/helpers/search-result-url.helper";
import { generateApplyURL } from "../../../../../util/generate-apply-url";

type Props = {
  course: ICourse;
  searchQueryId: string;
  searchPosition: number;
  isBreakpointLgUp: boolean;
  isBreakpointMediumUp: boolean;
  brandPrimaryColor: string;
  themeColor: string;
  invertedTheme: boolean;
  handleApplyClick: (toggleModal: boolean, courseName: string, courseApplyURL: string) => void;
};

function getSearchResultUrls(course: ICourse, locale: string) {
  let searchResultURL: SearchResultUrls = null;
  if (course.entryRoutes.includes(CourseType.SupportedDirectEntry) || course.mainProgram.slug !== null) {
    if (course.entryRoutes.includes(CourseType.DirectEntry) || course.mainProgram.slug === `international-direct`) {
      searchResultURL = getSearchResultURL(
        locale,
        process.env.NEXT_PUBLIC_INTO_STUDY_URL,
        course.brand.slug,
        course.brand.country,
        course.mainProgram.slug,
        true
      );
    } else {
      searchResultURL = getSearchResultURL(
        locale,
        process.env.NEXT_PUBLIC_INTO_STUDY_URL,
        course.brand.slug,
        course.brand.country,
        course.mainProgram.slug,
        false
      );
    }
  } else {
    searchResultURL = getSearchResultURL(
      locale,
      process.env.NEXT_PUBLIC_INTO_STUDY_URL,
      course.brand.slug,
      course.brand.country,
      course.mainProgram.slug,
      false
    );
  }
  return searchResultURL;
}

export default function SearchResultsRow({
  course,
  searchQueryId,
  searchPosition,
  isBreakpointLgUp,
  brandPrimaryColor,
  themeColor,
  invertedTheme,
  handleApplyClick
}: Props) {
  const locale = useLocale();

  const renderData = useMemo(() => {
    if (!course) {
      return null;
    }

    // Check program study level
    const isResultCardSimplified = [ProductType.Choice, ProductType.ALevel, ProductType.EnglishLanguage].includes(
      course.productType as ProductType
    );

    const isResultCardDE =
      course.entryRoutes?.includes(CourseType.DirectEntry) ||
      course.entryRoutes?.includes(CourseType.SupportedDirectEntry);

    const searchResultURL = getSearchResultUrls(course, locale);
    return { isResultCardSimplified, searchResultURL, isResultCardDE };
  }, [course, locale]);

  // Search Result Card
  return (
    <div
      key={course.mainProgram.id}
      className="result-row d-flex"
      data-insights-object-id={course.mainProgram.outcomeId}
      data-insights-position={searchPosition}
      data-insights-query-id={searchQueryId}
    >
      <div className="course d-flex flex-column w-100">
        <div className="d-flex px-3 px-lg-4">
          <div className="w-100">
            <MainProgramName mainProgramName={course.mainProgram.name} />
            <div>
              {/* Show University Brand Link only on Desktop View */}
              {isBreakpointLgUp ? (
                <UniversityName
                  universityName={course.brand.name}
                  universityURL={renderData.searchResultURL.university}
                />
              ) : (
                <h6 className="font-family-lato fw-normal d-inline-block mb-0">{course.brand.name}</h6>
              )}
              <h6 className="brand-location font-family-lato fw-normal d-inline-block mb-0">
                <span>{course?.brand?.country}</span>
              </h6>
            </div>
          </div>
        </div>
        {/* Desktop View for Program Options Header */}
        {isBreakpointLgUp && (
          <div className="d-flex mt-md-3 mt-2 mb-2 px-3 px-lg-4">
            <div className="col-4">
              <h6 className="font-family-lato fw-normal m-0 text-color-muted">
                {renderData.isResultCardSimplified ? `Program` : `Entry Routes`}
              </h6>
            </div>
            <div className="col-4">
              <h6
                className={`${
                  course.entryRoutes?.includes(CourseType.DirectEntry) ||
                  course.entryRoutes?.includes(CourseType.SupportedDirectEntry) ||
                  renderData.isResultCardSimplified
                    ? `visually-hidden`
                    : null
                } font-family-lato fw-normal m-0 text-color-muted`}
              >
                Next 3 Start Dates
              </h6>
            </div>
            <div className="col-4">
              <h6 className="visually-hidden m-0 text-color-muted">
                Choose program for comparison or directly go to Apply form
              </h6>
            </div>
          </div>
        )}
        {/* Desktop view for Program Options */}
        {isBreakpointLgUp && (
          <ProgramOptions
            course={course}
            isBreakpointLgUp={isBreakpointLgUp}
            isResultCardSimplified={renderData.isResultCardSimplified}
            searchResultURL={renderData.searchResultURL}
            isResultCardDE={renderData.isResultCardDE}
            brandPrimaryColor={brandPrimaryColor}
            themeColor={themeColor}
            invertedTheme={invertedTheme}
            handleApplyClick={handleApplyClick}
          />
        )}
        {/* Mobile view for Program Options */}
        {!isBreakpointLgUp && (
          <Accordion alwaysOpen bsPrefix="search-results-accordion mt-2">
            <Accordion.Item eventKey="0" bsPrefix="search-results-accordion-item">
              <Accordion.Body bsPrefix="search-results-accordion-body pt-3 mb-2">
                {" "}
                <ProgramOptions
                  course={course}
                  isBreakpointLgUp={isBreakpointLgUp}
                  isResultCardSimplified={renderData.isResultCardSimplified}
                  searchResultURL={renderData.searchResultURL}
                  isResultCardDE={renderData.isResultCardDE}
                  brandPrimaryColor={brandPrimaryColor}
                  themeColor={themeColor}
                  invertedTheme={invertedTheme}
                  handleApplyClick={handleApplyClick}
                />
              </Accordion.Body>
              <Accordion.Header bsPrefix="search-results-accordion-header px-3">
                {renderData.isResultCardSimplified ? `Program` : `Entry Routes`}
              </Accordion.Header>
            </Accordion.Item>
          </Accordion>
        )}
      </div>
    </div>
  );
}

// get program name for DE / SDE
const getCourseNameToDisplay = (course: ICourse): string => {
  if (
    course.entryRoutes?.includes(CourseType.DirectEntry) &&
    (course.mainProgram.slug == null || course.entryRoutes?.includes(CourseType.DirectEntry))
  ) {
    return CourseType.DirectEntry;
  }

  if (course.entryRoutes?.includes(CourseType.SupportedDirectEntry)) {
    if (course.mainProgram.programGroup !== null) {
      return course.mainProgram.programGroup;
    }

    if (course.mainProgram.name.includes(CourseType.MastersInternationalDirect)) {
      return CourseType.MastersInternationalDirect;
    }

    if (course.mainProgram.name.includes(CourseType.InternationalDirect)) {
      return CourseType.InternationalDirect;
    }

    if (
      course.mainProgram.name.includes(CourseType.IntegratedMasterProgram) ||
      course.mainProgram.name.includes(CourseType.IntegratedMastersProgram)
    ) {
      return CourseType.IntegratedMasterProgram;
    }
  }

  return course.mainProgram.name;
};

export type ProgramOptionsProps = {
  course: ICourse;
  isBreakpointLgUp: boolean;
  isResultCardSimplified: boolean;
  searchResultURL: SearchResultUrls;
  isResultCardDE: boolean;
  brandPrimaryColor: string;
  themeColor: string;
  invertedTheme: boolean;
  handleApplyClick: (toggleModal: boolean, courseName: string, courseApplyURL: string) => void;
};

// List of EntryRoutes with
// Next Intakes
// Compare Feature
// Apply Button
const ProgramOptions: FC<ProgramOptionsProps> = ({
  course,
  isBreakpointLgUp,
  isResultCardSimplified,
  searchResultURL,
  isResultCardDE,
  brandPrimaryColor,
  themeColor,
  invertedTheme,
  handleApplyClick
}) => {
  const translate = useTranslations(`Common`);

  return (
    <>
      {(isResultCardDE || isResultCardSimplified) && (
        <div className="program-row d-flex justify-content-between flex-lg-row flex-column">
          <div className="col-lg-4 col-12 mb-3 mb-lg-0">
            <h4 className="m-0 font-family-lato fw-normal d-flex">
              {course?.mainProgram?.disableCourseLink ? (
                <>{getCourseNameToDisplay(course)}</>
              ) : (
                <Link
                  href={
                    course.entryRoutes?.includes(CourseType.DirectEntry) &&
                    (course.mainProgram.slug == null || course.mainProgram.slug === `direct-entry`)
                      ? searchResultURL.directEntry
                      : searchResultURL.program
                  }
                >
                  {getCourseNameToDisplay(course)}
                </Link>
              )}
            </h4>
          </div>
          {/* TODO: Add Intakes for DE / SDE / A-level / English Language */}
          {isBreakpointLgUp && (
            <div className="col-lg-4 col-12 mb-3 mb-lg-0">
              <h6 className="visually-hidden">Placeholder for DE Next 3 Start Dates</h6>
            </div>
          )}
          <div className="col-lg-4 col-12 d-flex justify-content-lg-end justify-content-between">
            <div>
              <Button
                buttonStyle={!isBreakpointLgUp ? ButtonStyleType.Outline : ButtonStyleType.Text}
                buttonSize={ButtonSizeType.Medium}
                copyColor={invertedTheme ? brandPrimaryColor : themeColor}
                outlineColor={invertedTheme ? brandPrimaryColor : themeColor}
                backgroundColor={color.white}
                hoverBackgroundColor={invertedTheme ? brandPrimaryColor : themeColor}
                activeBackgroundColor={invertedTheme ? brandPrimaryColor : themeColor}
                buttonID={`${course.mainProgram.slug}-${course.mainProgram.outcomeId}`}
                buttonAriaLabel={`Apply Program Link for ${course.mainProgram.name}`}
                onClick={() =>
                  handleApplyClick(
                    true,
                    course.mainProgram.name,
                    generateApplyURL(course.brand.id, course.mainProgram.id)
                  )
                }
              >
                {translate(`header.nav.apply`)}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* List of Pathway programs */}
      {course.programs?.length > 0 && (
        <PathwayOptions
          courseOptions={course.programs}
          brandId={course.brand.id}
          brandName={course.brand.name}
          brandCountry={course.brand.country}
          brandPrimaryColor={brandPrimaryColor}
          themeColor={themeColor}
          invertedTheme={invertedTheme}
          handleApplyClick={handleApplyClick}
        />
      )}
    </>
  );
};
